import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

let csrfNonce = null;
let csrfPromise = null;
const unsafeMethods = new Set(["post", "put", "patch", "delete"]);
async function loadCsrf() {
  if (csrfNonce) return csrfNonce;
  if (!csrfPromise) {
    csrfPromise = api.get("/auth/csrf").then(({ data }) => {
      csrfNonce = data.nonce;
      return csrfNonce;
    }).finally(() => { csrfPromise = null; });
  }
  return csrfPromise;
}
api.interceptors.request.use(async (config) => {
  if (unsafeMethods.has((config.method || "get").toLowerCase())) {
    config.headers["X-CSRF-Token"] = await loadCsrf();
  }
  return config;
});

// Global 401 handler: if session is invalid (e.g., user was deleted, token expired),
// clear client state and bounce to /login. Skips auth/* endpoints so login errors
// still surface normally.
let _sessionExpiredHandled = false;
api.interceptors.response.use(
  (r) => {
    if (/\/auth\/(verify-otp|login-pin|login|logout|change-password)(\?|$)/.test(r.config?.url || "")) csrfNonce = null;
    return r;
  },
  async (err) => {
    const status = err?.response?.status;
    if (status === 403 && err.response?.data?.code === "csrf_failed" && err.config && !err.config._csrfRetry) {
      csrfNonce = null;
      err.config._csrfRetry = true;
      return api.request(err.config);
    }
    const url = err?.config?.url || "";
    const isAuthEndpoint = url.includes("/auth/login") ||
                           url.includes("/auth/verify-otp") ||
                           url.includes("/auth/resend-otp") ||
                           url.includes("/auth/me") ||
                           url.includes("/auth/register");
    if (status === 401 && !isAuthEndpoint && !_sessionExpiredHandled) {
      _sessionExpiredHandled = true;
      try {
        // Best-effort server-side cookie clear
        api.post("/auth/logout").catch(() => {});
      } catch {}
      // Purge cached per-session state so the next login starts clean
      try {
        localStorage.removeItem("nf_company");
      } catch {}
      // Small delay so the toast (if any) can render before redirect
      setTimeout(() => {
        _sessionExpiredHandled = false;
        if (typeof window !== "undefined" && !window.location.pathname.startsWith("/giris")) {
          window.location.href = "/giris?reason=session_expired";
        }
      }, 100);
    }
    return Promise.reject(err);
  }
);

export function formatApiErrorDetail(detail) {
  const fallback = "İşleminiz tamamlanamadı. Lütfen tekrar deneyin; sorun sürerse yöneticinizle iletişime geçin.";
  if (detail == null) return fallback;
  if (typeof detail === "string") {
    if (/network error|failed to fetch|timeout|timed out/i.test(detail)) return "Sunucuya ulaşılamadı. Bağlantınızı kontrol edip tekrar deneyin.";
    if (/traceback|TypeError|ReferenceError|removeChild|ObjectId|\[object Object\]|Request failed with status code|Internal Server Error/i.test(detail)) return fallback;
    return detail;
  }
  if (Array.isArray(detail)) {
    const fields = { email: "E-posta", password: "Şifre", company_id: "Şirket", amount: "Tutar", miktar: "Miktar", due_date: "Vade tarihi", date: "Tarih", name: "Ad", phone: "Telefon", code: "Doğrulama kodu" };
    return detail.map((e) => {
      const name = fields[e?.loc?.at(-1)] || "Alan";
      if (e?.type) return `${name}: ${e.type === "missing" ? "Zorunlu alanı doldurun." : "Geçerli bir değer girin."}`;
      return formatApiErrorDetail(e?.message || e?.msg || null);
    }).filter(Boolean).join(" ") || fallback;
  }
  if (typeof detail.message === "string") return formatApiErrorDetail(detail.message);
  if (typeof detail.msg === "string") return formatApiErrorDetail(detail.msg);
  return fallback;
}
